import React, { useState } from 'react';
import { Info, HelpCircle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';

/**
 * FAQInfoPopup - Accordion-style documentation/help dialog
 */
export const FAQInfoPopup = () => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="faq-info-button">
          <Info className="h-4 w-4 mr-2" />
          Help
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            Payment Verification Guide
          </DialogTitle>
          <DialogDescription>
            Understanding the validation logic and workflow
          </DialogDescription>
        </DialogHeader>
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="calculation">
            <AccordionTrigger>How are the values calculated?</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p><strong>Three-step calculation:</strong></p>
              <ol className="list-decimal pl-4 space-y-2">
                <li><strong>Usage Charges:</strong> Taken directly from External DB's "newcharges" field (as-is, uncalculated)</li>
                <li><strong>New Charges:</strong> = Usage Charges + Late Fee - Credits (NO BBF)</li>
                <li><strong>Funds Calculated:</strong> = New Charges + BBF (if applicable based on sequencing)</li>
              </ol>
              <p className="text-muted-foreground mt-2">BBF is only added when billing sequence and 25-day rule criteria are met.</p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="bbf-rule">
            <AccordionTrigger>What is the BBF (25-day) Rule?</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p>BBF is included only when:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Bill is NOT the first bill (SECOND_BILL or THIRD_PLUS)</li>
                <li>Gap between bills exceeds 25 days</li>
                <li>Current issue date is past the previous bill's due date</li>
              </ul>
              <p className="text-muted-foreground mt-2">This prevents double-counting previous balances when bills are issued close together.</p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="billing-sequence">
            <AccordionTrigger>What do FIRST_BILL, SECOND_BILL, THIRD_PLUS mean?</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <ul className="list-disc pl-4 space-y-1">
                <li><strong>FIRST_BILL:</strong> No previous bills found for this account - BBF is never included</li>
                <li><strong>SECOND_BILL:</strong> One previous bill exists - BBF may be included based on 25-day rule</li>
                <li><strong>THIRD_PLUS:</strong> Two or more previous bills exist - BBF may be included based on 25-day rule</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="validation-status">
            <AccordionTrigger>What do the validation badges mean?</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <ul className="list-disc pl-4 space-y-1">
                <li><strong>V (Green):</strong> Verified - Bill found in external database</li>
                <li><strong>N (Grey):</strong> Not found in external database</li>
                <li><strong>C (Orange):</strong> Corrected - Values adjusted from original CSV</li>
                <li><strong>[M] (Blue):</strong> Child account linked to a Master account</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="ops-approval">
            <AccordionTrigger>How does Operational Approval work?</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p>The checkmark (✓) at the end of each row indicates approval status:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li><strong>Grey ✓:</strong> Not yet approved - Click to approve</li>
                <li><strong>Green ✓:</strong> Operationally approved - Ready for customer review</li>
                <li><strong>Green ✓✓:</strong> Fully approved (Ops + Customer)</li>
              </ul>
              <p className="text-muted-foreground mt-2">Use "Bulk Approve" to approve all items at once.</p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="mismatch">
            <AccordionTrigger>What happens when totals don't match?</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p>Two types of mismatches are detected:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li><strong>Sum Mismatch:</strong> If sum of Calculated Funds ≠ Total Requested Amount, the header is highlighted in red</li>
                <li><strong>DB Mismatch:</strong> If TotalAmountDue differs from master bill in DB, it's flagged but not auto-corrected</li>
              </ul>
              <p className="text-muted-foreground mt-2">Check the Method popup for detailed calculation breakdown.</p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="download">
            <AccordionTrigger>What do the Download options do?</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p><strong>Two download options available:</strong></p>
              <ul className="list-disc pl-4 space-y-1">
                <li><strong>Screen Data Only:</strong> Downloads only the data currently visible on screen with calculated values</li>
                <li><strong>Full Data with Corrections:</strong> Downloads all original columns from the source CSV with corrected values merged in</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="filters">
            <AccordionTrigger>How do I filter the table data?</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p>Click the <strong>filter icon</strong> next to column headers (Account, Supplier, Commodity, GL Code) to filter data.</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Search within the dropdown to find specific values</li>
                <li>Select multiple values to include (multi-select)</li>
                <li>Click "Clear Filters" in the stats bar to reset all filters</li>
              </ul>
              <p className="text-muted-foreground mt-2">Filters work on currently loaded data. Load more rows to expand filter options.</p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="notes">
            <AccordionTrigger>How do Invoice Notes work?</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p>Click the <strong>note icon</strong> on any row to add, view, or edit notes.</p>
              <ul className="list-disc pl-4 space-y-1">
                <li><strong>Blue dot indicator:</strong> Shows when a note exists</li>
                <li>Notes are saved with timestamp and user info</li>
                <li>Multiple notes can be added per invoice</li>
                <li>Edit or delete existing notes anytime</li>
              </ul>
              <p className="text-muted-foreground mt-2">Use notes to track issues, corrections, or communication about specific invoices.</p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="reprocess">
            <AccordionTrigger>What is Reprocessing and when should I use it?</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p><strong>Reprocessing re-validates invoices against the external database.</strong></p>
              <div className="bg-muted/50 rounded-md p-2 mt-2">
                <p className="font-medium">Options:</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li><strong>Reprocess Errors Only (Recommended):</strong> Only re-validates failed rows - keeps your corrections safe</li>
                  <li><strong>Reprocess All:</strong> Re-validates everything - <span className="text-red-600">overwrites all manual corrections</span></li>
                  <li><strong>Row-level Reprocess:</strong> Use ⋮ menu on specific rows to reprocess individually</li>
                </ul>
              </div>
              <p className="text-muted-foreground mt-2">Use "Reprocess Errors Only" after fixing data issues in the source system.</p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="bill-copy">
            <AccordionTrigger>How do I view the original bill PDF?</AccordionTrigger>
            <AccordionContent className="text-sm space-y-2">
              <p>Click the <strong>PDF icon</strong> at the start of any row to view the bill copy.</p>
              <ul className="list-disc pl-4 space-y-1">
                <li><strong>Blue icon:</strong> Bill copy available - click to view</li>
                <li><strong>Grey icon:</strong> No bill copy available in the system</li>
              </ul>
              <p className="text-muted-foreground mt-2">The PDF viewer opens on the left side. The corresponding row is highlighted for easy reference.</p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </DialogContent>
    </Dialog>
  );
};

export default FAQInfoPopup;
